import { Badge, Button, cn, Select } from "@artisan/ui";
import {
  Box,
  Check,
  ChevronLeft,
  CircleAlert,
  DoorOpen,
  Layers,
  Plus,
  Ruler,
  Sparkles,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";

import { ElevationView } from "../components/elevation-view";
import { IsoView } from "../components/iso-view";
import { MeasureInput, MmInput } from "../components/measure-input";
import { PlanView } from "../components/plan-view";
import { projectTitle } from "../project/actions";
import {
  cornerLabel,
  type M,
  nextId,
  OBSTRUCTION_KINDS,
  OPENING_KINDS,
  type Project,
  PURPOSES,
  type RoomDraft,
  STANDARD_WIDTHS_MM,
  UTILITY_KINDS,
  wallLabel,
  ZONE_DEFAULTS,
  ZONE_KINDS,
  type ZoneDraft,
} from "../project/model";
import { updateProject, useProject } from "../project/store";
import { autoPartitions } from "../survey/partitions";
import { type FieldIssue, reviewRoom } from "../survey/checks";
import { areaM2 } from "../survey/geometry";
import { planDiagonals, solveRoom } from "../survey/solve";
import type { ObstructionKind, OpeningKind, PartitionPurpose, UtilityKind, ZoneKind } from "../survey/types";
import { formatDelta, formatMm } from "../survey/units";

type Mode = "measure" | "items" | "zones" | "3d";

const MODES: { key: Mode; label: string; icon: typeof Ruler }[] = [
  { key: "measure", label: "Хэмжилт", icon: Ruler },
  { key: "items", label: "Нээлхий, цэг", icon: DoorOpen },
  { key: "zones", label: "Бүс", icon: Layers },
  { key: "3d", label: "3D", icon: Box },
];

function useRoom() {
  const { id, roomId } = useParams();
  const project = useProject(id);
  const room = project?.rooms.find((r) => r.id === roomId);
  const edit = (fn: (room: RoomDraft, p: Project) => void) => {
    if (!id) return;
    void updateProject(id, (p) => {
      const r = p.rooms.find((x) => x.id === roomId);
      if (r) fn(r, p);
    });
  };
  return { project, room, edit };
}

/** Run fn on the item with this id, if it is still there. */
function withId<T extends { id: string }>(list: T[], id: string, fn: (item: T) => void) {
  const item = list.find((x) => x.id === id);
  if (item) fn(item);
}

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function WallChips({ room, wall, onChange }: { room: RoomDraft; wall: number; onChange: (i: number) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Хана сонгох">
      {room.sketch.map((_, i) => (
        <button
          key={i}
          type="button"
          role="radio"
          aria-checked={wall === i}
          aria-label={`${wallLabel(i)} хана`}
          onClick={() => onChange(i)}
          className={cn(
            "h-10 min-w-10 rounded-lg border px-3 font-semibold",
            wall === i ? "border-primary bg-primary text-primary-foreground" : "bg-card hover:bg-accent",
          )}
        >
          {wallLabel(i)}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ measure panel

function MeasurePanel({ room, wall, setWall, edit }: { room: RoomDraft; wall: number; setWall: (i: number) => void; edit: ReturnType<typeof useRoom>["edit"] }) {
  const plan = planDiagonals(room.sketch);
  const planned = [...plan.fix, ...(plan.check ? [plan.check] : [])];
  const rows = [...planned.map(([a, b]) => ({ a, b, planned: true }))];
  for (const d of room.diagonals) if (!rows.some((r) => r.a === d.a && r.b === d.b)) rows.push({ a: d.a, b: d.b, planned: false });
  const [extraA, setExtraA] = useState(0);
  const [extraB, setExtraB] = useState(2);
  const n = room.sketch.length;
  const setReading = (i: number, k: number, m: M) =>
    edit((r) => {
      const readings = (r.readings[i] ??= [null, null, null]);
      readings[k] = m;
      const filled = readings.filter((x): x is NonNullable<M> => Boolean(x));
      if (filled.length) r.walls[i] = filled.reduce((a, b) => (b.um < a.um ? b : a));
    });

  return (
    <div className="space-y-6">
      <Section title="Ханын урт · цагийн зүүний дагуу">
        <div className="space-y-2">
          {room.walls.map((w, i) => {
            const open = wall === i;
            return (
              <div key={i} className={cn("rounded-xl border bg-card p-2.5", open && "border-primary shadow-xs")}>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setWall(i)}
                    className={cn("grid size-10 shrink-0 place-items-center rounded-lg font-semibold", open ? "bg-primary text-primary-foreground" : "bg-muted")}
                    aria-label={`${wallLabel(i)} хана сонгох`}
                  >
                    {wallLabel(i)}
                  </button>
                  <MeasureInput
                    className="flex-1"
                    label={`${cornerLabel(i)} → ${cornerLabel((i + 1) % n)} булан`}
                    value={w}
                    onChange={(m) =>
                      edit((r) => {
                        r.walls[i] = m;
                        r.readings[i] = [null, null, null];
                      })
                    }
                  />
                </div>
                {open ? (
                  <div className="mt-3 space-y-3 border-t pt-3">
                    <p className="text-xs text-muted-foreground">3 өндөрт хэмжвэл хамгийн богиныг ханын урт болгоно.</p>
                    <div className="grid grid-cols-3 gap-2">
                      {["Шалны", "Дунд", "Таазны"].map((label, k) => (
                        <MeasureInput key={k} label={label} value={room.readings[i]?.[k] ?? null} onChange={(m) => setReading(i, k, m)} showSource={false} />
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[0, 1].map((k) => (
                        <MeasureInput
                          key={k}
                          label={k === 0 ? "Өндөр (шал–тааз)" : "Өндөр, 2-р цэг"}
                          value={room.heights[i]?.[k] ?? null}
                          showSource={false}
                          onChange={(m) =>
                            edit((r) => {
                              const hs = [...(r.heights[i] ?? [])];
                              if (m) hs[k] = m;
                              else hs.splice(k, 1);
                              r.heights[i] = hs.filter(Boolean);
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Таазны өндөр">
        <MeasureInput label="Хамгийн нам цэг" value={room.ceiling} onChange={(m) => edit((r) => (r.ceiling = m))} />
      </Section>

      <Section title="Диагональ · өнцгийг тогтооно">
        <p className="text-xs text-muted-foreground">
          {n - 3} диагональ өнцгийг тогтооно, дахин нэг нь өрөө битүүрэхийг шалгана. Өнцгийг 90° гэж таамаглахгүй.
        </p>
        <div className="space-y-2">
          {rows.map(({ a, b, planned: isPlanned }) => {
            const d = room.diagonals.find((x) => x.a === a && x.b === b);
            const isCheck = plan.check && plan.check[0] === a && plan.check[1] === b;
            return (
              <div key={`${a}-${b}`} className="flex items-end gap-2">
                <MeasureInput
                  className="flex-1"
                  label={`${cornerLabel(a)} – ${cornerLabel(b)}${isCheck ? " · шалгах" : isPlanned ? "" : " · нэмэлт"}`}
                  value={d?.length ?? null}
                  onChange={(m) =>
                    edit((r) => {
                      const existing = r.diagonals.find((x) => x.a === a && x.b === b);
                      if (existing) existing.length = m;
                      else if (m) r.diagonals.push({ a, b, length: m });
                      if (!m && !isPlanned) r.diagonals = r.diagonals.filter((x) => !(x.a === a && x.b === b));
                    })
                  }
                />
              </div>
            );
          })}
        </div>
        {n > 3 ? (
          <div className="flex items-end gap-2">
            <label className="grid gap-1 text-xs text-muted-foreground">
              Булан
              <Select value={extraA} onChange={(e) => setExtraA(Number(e.target.value))} aria-label="Эхний булан">
                {room.sketch.map((_, i) => (
                  <option key={i} value={i}>
                    {cornerLabel(i)}
                  </option>
                ))}
              </Select>
            </label>
            <label className="grid gap-1 text-xs text-muted-foreground">
              Булан
              <Select value={extraB} onChange={(e) => setExtraB(Number(e.target.value))} aria-label="Хоёр дахь булан">
                {room.sketch.map((_, i) => (
                  <option key={i} value={i}>
                    {cornerLabel(i)}
                  </option>
                ))}
              </Select>
            </label>
            <Button
              variant="outline"
              icon={Plus}
              disabled={extraA === extraB || Math.abs(extraA - extraB) === 1 || Math.abs(extraA - extraB) === n - 1 || rows.some((r) => r.a === Math.min(extraA, extraB) && r.b === Math.max(extraA, extraB))}
              onClick={() =>
                edit((r) => {
                  const a = Math.min(extraA, extraB);
                  const b = Math.max(extraA, extraB);
                  r.diagonals.push({ a, b, length: null });
                })
              }
            >
              Диагональ нэмэх
            </Button>
          </div>
        ) : null}
      </Section>
    </div>
  );
}

// ------------------------------------------------------------------ items panel

function ItemsPanel({ room, wall, setWall, edit, selected, setSelected }: PanelProps) {
  const openings = room.openings.filter((o) => o.wall === wall);
  const utilities = room.utilities.filter((u) => u.wall === wall);
  const obstructions = room.obstructions.filter((o) => o.wall === wall);
  const del = (id: string) =>
    edit((r) => {
      r.openings = r.openings.filter((x) => x.id !== id);
      r.utilities = r.utilities.filter((x) => x.id !== id);
      r.obstructions = r.obstructions.filter((x) => x.id !== id);
    });
  return (
    <div className="space-y-6">
      <Section title="Хана">
        <WallChips room={room} wall={wall} onChange={setWall} />
        <p className="text-xs text-muted-foreground">
          Байрлалыг {cornerLabel(wall)}-р булангаас (хананы эхлэл, зүүн тал) хэмжинэ.
        </p>
      </Section>
      <Section
        title="Нээлхий"
        aside={
          <AddMenu
            options={OPENING_KINDS}
            onAdd={(kind) =>
              edit((r, p) => {
                const id = nextId(p, `${r.id}.O`);
                r.openings.push({ id, kind: kind as OpeningKind, wall, offset: null, width: null, sill: kind === "door" ? { um: 0, source: "laser" } : null, height: null });
                setSelected(id);
              })
            }
          />
        }
      >
        {openings.map((o) => (
          <ItemCard key={o.id} title={`${OPENING_KINDS[o.kind]} · ${o.id.split(".").pop()}`} open={selected === o.id} onOpen={() => setSelected(o.id)} onDelete={() => del(o.id)}>
            <div className="grid grid-cols-2 gap-2">
              <MeasureInput label="Булангаас" value={o.offset} onChange={(m) => edit((r) => withId(r.openings, o.id, (x) => (x.offset = m)))} />
              <MeasureInput label="Өргөн" value={o.width} onChange={(m) => edit((r) => withId(r.openings, o.id, (x) => (x.width = m)))} />
              <MeasureInput label="Шалнаас доод ирмэг" value={o.sill} onChange={(m) => edit((r) => withId(r.openings, o.id, (x) => (x.sill = m)))} />
              <MeasureInput label="Өндөр" value={o.height} onChange={(m) => edit((r) => withId(r.openings, o.id, (x) => (x.height = m)))} />
            </div>
          </ItemCard>
        ))}
      </Section>
      <Section
        title="Инженерийн цэг"
        aside={
          <AddMenu
            options={UTILITY_KINDS}
            onAdd={(kind) =>
              edit((r, p) => {
                const id = nextId(p, `${r.id}.U`);
                r.utilities.push({ id, kind: kind as UtilityKind, wall, offset: null, height: null });
                setSelected(id);
              })
            }
          />
        }
      >
        {utilities.map((u) => (
          <ItemCard key={u.id} title={`${UTILITY_KINDS[u.kind]} · ${u.id.split(".").pop()}`} open={selected === u.id} onOpen={() => setSelected(u.id)} onDelete={() => del(u.id)}>
            <div className="grid grid-cols-2 gap-2">
              <MeasureInput label="Булангаас (төв)" value={u.offset} onChange={(m) => edit((r) => withId(r.utilities, u.id, (x) => (x.offset = m)))} />
              <MeasureInput label="Шалнаас (төв)" value={u.height} onChange={(m) => edit((r) => withId(r.utilities, u.id, (x) => (x.height = m)))} />
            </div>
          </ItemCard>
        ))}
      </Section>
      <Section
        title="Саад: багана, хоолой, радиатор"
        aside={
          <AddMenu
            options={OBSTRUCTION_KINDS}
            onAdd={(kind) =>
              edit((r, p) => {
                const id = nextId(p, `${r.id}.X`);
                r.obstructions.push({ id, kind: kind as ObstructionKind, wall, offset: null, width: null, elevation: { um: 0, source: "laser" }, height: null, depth: null });
                setSelected(id);
              })
            }
          />
        }
      >
        {obstructions.map((o) => {
          const set = (key: "offset" | "width" | "elevation" | "height" | "depth") => (m: M) => edit((r) => withId(r.obstructions, o.id, (x) => (x[key] = m)));
          return (
            <ItemCard key={o.id} title={`${OBSTRUCTION_KINDS[o.kind]} · ${o.id.split(".").pop()}`} open={selected === o.id} onOpen={() => setSelected(o.id)} onDelete={() => del(o.id)}>
              <div className="grid grid-cols-2 gap-2">
                <MeasureInput label="Булангаас" value={o.offset} onChange={set("offset")} />
                <MeasureInput label="Өргөн" value={o.width} onChange={set("width")} />
                <MeasureInput label="Шалнаас" value={o.elevation} onChange={set("elevation")} />
                <MeasureInput label="Өндөр" value={o.height} onChange={set("height")} />
                <MeasureInput label="Ханаас гарсан" value={o.depth} onChange={set("depth")} />
              </div>
            </ItemCard>
          );
        })}
      </Section>
    </div>
  );
}

function AddMenu<K extends string>({ options, onAdd }: { options: Record<K, string>; onAdd: (k: K) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button variant="outline" size="sm" icon={Plus} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        Нэмэх
      </Button>
      {open ? (
        <div className="absolute top-9 right-0 z-10 w-44 rounded-lg border bg-popover p-1 shadow-lg">
          {(Object.keys(options) as K[]).map((k) => (
            <button
              key={k}
              type="button"
              className="block w-full rounded-md px-3 py-2.5 text-left text-sm hover:bg-accent"
              onClick={() => {
                setOpen(false);
                onAdd(k);
              }}
            >
              {options[k]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ItemCard({ title, open, onOpen, onDelete, children }: { title: string; open: boolean; onOpen: () => void; onDelete: () => void; children: ReactNode }) {
  return (
    <div className={cn("rounded-xl border bg-card", open && "border-primary shadow-xs")}>
      <div className="flex items-center gap-2 p-2.5 pl-3.5">
        <button type="button" className="flex-1 text-left font-medium" onClick={onOpen}>
          {title}
        </button>
        <Button variant="ghost" size="icon-sm" aria-label={`${title} устгах`} onClick={onDelete}>
          <Trash2 aria-hidden />
        </Button>
      </div>
      {open ? <div className="border-t p-3">{children}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------------ zones panel

interface PanelProps {
  room: RoomDraft;
  wall: number;
  setWall: (i: number) => void;
  edit: ReturnType<typeof useRoom>["edit"];
  selected: string | null;
  setSelected: (id: string | null) => void;
}

function ZonesPanel({ room, wall, setWall, edit, selected, setSelected }: PanelProps) {
  const zones = room.zones.filter((z) => z.wall === wall);
  const zone = room.zones.find((z) => z.id === selected) ?? zones[0] ?? null;
  const [width, setWidth] = useState(600);
  const wallUm = room.walls[wall]?.um ?? 0;
  const setZone = (fn: (z: ZoneDraft) => void) => {
    if (zone) edit((r) => withId(r.zones, zone.id, fn));
  };
  return (
    <div className="space-y-6">
      <Section title="Хана">
        <WallChips
          room={room}
          wall={wall}
          onChange={(i) => {
            setWall(i);
            setSelected(null);
          }}
        />
      </Section>
      <Section
        title="Бүс"
        aside={
          <AddMenu
            options={ZONE_KINDS}
            onAdd={(kind) =>
              edit((r, p) => {
                const k = kind as ZoneKind;
                const id = nextId(p, `${r.id}.Z`);
                const d = ZONE_DEFAULTS[k];
                r.zones.push({ id, name: `${ZONE_KINDS[k]} · ${wallLabel(wall)}`, kind: k, wall, offset: 0, length: wallUm || 1_200_000, ...d, partitions: [] });
                setSelected(id);
              })
            }
          />
        }
      >
        <div className="flex flex-wrap gap-2">
          {zones.map((z) => (
            <button
              key={z.id}
              type="button"
              onClick={() => setSelected(z.id)}
              className={cn("rounded-lg border px-3 py-2 text-sm font-medium", zone?.id === z.id ? "border-primary bg-primary-soft text-primary" : "bg-card")}
            >
              {z.name || ZONE_KINDS[z.kind]}
            </button>
          ))}
          {zones.length === 0 ? <p className="text-sm text-muted-foreground">Энэ хананд бүс алга. Доод, дээд эсвэл өндөр бүс нэмнэ үү.</p> : null}
        </div>
      </Section>
      {zone && zone.wall === wall ? (
        <>
          <Section
            title="Хэмжээ, мм"
            aside={
              <Button variant="ghost" size="icon-sm" aria-label="Бүс устгах" onClick={() => edit((r) => (r.zones = r.zones.filter((z) => z.id !== zone.id)))}>
                <Trash2 aria-hidden />
              </Button>
            }
          >
            <div className="grid grid-cols-2 gap-2">
              <MmInput label="Булангаас" value={zone.offset} onChange={(v) => setZone((z) => (z.offset = v))} />
              <MmInput label="Урт" value={zone.length} onChange={(v) => setZone((z) => (z.length = v))} />
              <MmInput label="Шалнаас" value={zone.elevation} onChange={(v) => setZone((z) => (z.elevation = v))} />
              <MmInput label="Өндөр" value={zone.height} onChange={(v) => setZone((z) => (z.height = v))} />
              <MmInput label="Гүн" value={zone.depth} onChange={(v) => setZone((z) => (z.depth = v))} />
            </div>
          </Section>
          <Section title="Хуваалт">
            <div className="flex flex-wrap items-center gap-1.5">
              {STANDARD_WIDTHS_MM.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWidth(w)}
                  className={cn("h-9 rounded-md px-2.5 font-mono text-sm", width === w ? "bg-primary-soft text-primary" : "bg-muted")}
                  aria-pressed={width === w}
                >
                  {w}
                </button>
              ))}
            </div>
            <Button
              className="w-full"
              size="lg"
              icon={Sparkles}
              onClick={() => setZone((z) => (z.partitions = autoPartitions(z, width * 1000, `${z.id}.P`)))}
            >
              {width} мм-ээр автоматаар хуваах
            </Button>
            <div className="space-y-2">
              {zone.partitions.map((p, i) => (
                <div key={p.id} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                  <MmInput
                    label={`${i + 1}. өргөн`}
                    value={p.width}
                    onChange={(v) =>
                      setZone((z) => {
                        const part = z.partitions[i];
                        if (!part) return;
                        part.width = v;
                        // Partitions stay packed: each one after it starts where the previous ends.
                        let end = part.offset + part.width;
                        for (const q of z.partitions.slice(i + 1)) {
                          q.offset = end;
                          end += q.width;
                        }
                      })
                    }
                  />
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    Зориулалт
                    <Select value={p.purpose} onChange={(e) => setZone((z) => {
                          const part = z.partitions[i];
                          if (part) part.purpose = e.target.value as PartitionPurpose;
                        })}>
                      {(Object.keys(PURPOSES) as PartitionPurpose[]).map((k) => (
                        <option key={k} value={k}>
                          {PURPOSES[k]}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Хуваалт устгах"
                    onClick={() =>
                      setZone((z) => {
                        z.partitions.splice(i, 1);
                        let x = 0;
                        for (const part of z.partitions) {
                          part.offset = x;
                          x += part.width;
                        }
                      })
                    }
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
              ))}
            </div>
          </Section>
        </>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ screen

function ClosureBadge({ room }: { room: RoomDraft }) {
  const r = solveRoom({
    sketch: room.sketch,
    walls: room.walls.map((w) => w?.um ?? null),
    diagonals: room.diagonals.map((d) => ({ a: d.a, b: d.b, um: d.length?.um ?? null })),
  });
  if (!r.ok) {
    const missing = r.reason === "missing_walls" ? r.walls.length : 0;
    return <Badge variant="secondary">{missing ? `${missing} хана хэмжих` : "Хэлбэр тодорхойгүй"}</Badge>;
  }
  const worst = r.checks.filter((c) => c.edge.kind === "diagonal").reduce((m, c) => Math.max(m, Math.abs(c.delta)), -1);
  if (worst < 0) return <Badge variant="warning">Шалгах диагональ алга</Badge>;
  if (worst > 5000) return <Badge variant="destructive" icon={CircleAlert}>Битүүрэхгүй · {formatDelta(worst)}</Badge>;
  return (
    <Badge variant="success" icon={Check}>
      Битүүрсэн · ±{formatDelta(worst)}
    </Badge>
  );
}

function IssueList({ issues }: { issues: FieldIssue[] }) {
  if (!issues.length)
    return (
      <p className="flex items-center gap-2 text-sm text-success">
        <Check className="size-4" aria-hidden /> Энэ өрөөний хэмжилт бүрэн.
      </p>
    );
  return (
    <ul className="space-y-2">
      {issues.slice(0, 6).map((i, k) => (
        <li key={k} className="flex gap-2 text-[13px] leading-snug">
          {i.severity === "error" ? (
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-label="Алдаа" />
          ) : (
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-label="Анхааруулга" />
          )}
          <span>{i.message}</span>
        </li>
      ))}
      {issues.length > 6 ? <li className="text-xs text-muted-foreground">+{issues.length - 6} бусад</li> : null}
    </ul>
  );
}

export function RoomScreen() {
  const { project, room, edit } = useRoom();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get("view") === "3d" ? "3d" : "measure");
  const [wall, setWall] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  if (!project || !room) return <Navigate to="/" replace />;
  const issues = reviewRoom(room);
  const plan = planDiagonals(room.sketch);
  const solved = solveRoom({
    sketch: room.sketch,
    walls: room.walls.map((w) => w?.um ?? null),
    diagonals: room.diagonals.map((d) => ({ a: d.a, b: d.b, um: d.length?.um ?? null })),
  });
  const panelProps: PanelProps = { room, wall, setWall, edit, selected, setSelected };

  return (
    <div className="flex h-full flex-col bg-drafting-grid">
      <header className="flex items-center gap-4 border-b bg-background/90 px-5 py-3 backdrop-blur">
        <Link to={`/p/${project.id}`} aria-label="Төсөл рүү буцах" className="grid size-11 place-items-center rounded-full border bg-card shadow-xs hover:bg-accent">
          <ChevronLeft className="size-5" aria-hidden />
        </Link>
        <div className="min-w-0">
          <input
            aria-label="Өрөөний нэр"
            className="w-56 truncate bg-transparent font-display text-[20px] font-semibold tracking-tight outline-none focus:underline"
            defaultValue={room.name}
            onBlur={(e) => e.target.value.trim() && e.target.value !== room.name && edit((r) => (r.name = e.target.value.trim()))}
          />
          <p className="truncate text-xs text-muted-foreground">{projectTitle(project)}</p>
        </div>
        <nav className="mx-auto flex rounded-xl border bg-muted p-1" aria-label="Горим">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              aria-pressed={mode === m.key}
              className={cn(
                "flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium",
                mode === m.key ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <m.icon className="size-4" aria-hidden /> {m.label}
            </button>
          ))}
        </nav>
        <ClosureBadge room={room} />
      </header>
      <div className="grid min-h-0 flex-1 grid-cols-[380px_1fr]">
        <aside className="min-h-0 overflow-y-auto border-r bg-background/95 p-5" aria-label="Хэмжилт оруулах">
          {mode !== "measure" ? (
            <div className="mb-4 h-40 rounded-xl border bg-card p-1">
              <PlanView room={room} selectedWall={wall} onSelectWall={setWall} compact className="h-full w-full" label="Өрөөний план: хана сонгох" />
            </div>
          ) : null}
          {mode === "measure" ? <MeasurePanel room={room} wall={wall} setWall={setWall} edit={edit} /> : null}
          {mode === "items" ? <ItemsPanel {...panelProps} /> : null}
          {mode === "zones" || mode === "3d" ? <ZonesPanel {...panelProps} /> : null}
        </aside>
        <div className="relative flex min-h-0 flex-col">
          {/* Room at the bottom for the issue and area cards that float over the drawing. */}
          <div className="min-h-0 flex-1 p-6 pb-32">
            {mode === "3d" ? (
              <IsoView room={room} selectedZone={selected} className="h-full w-full" />
            ) : mode === "measure" ? (
              <PlanView room={room} selectedWall={wall} onSelectWall={setWall} planned={[...plan.fix, ...(plan.check ? [plan.check] : [])]} className="h-full w-full" />
            ) : (
              // The wall being worked on fills the area (the plan is a small map in the panel).
              <div className="h-full rounded-xl border bg-card/80 p-3">
                <ElevationView room={room} wall={wall} selected={selected} onSelect={setSelected} className="h-full w-full" />
              </div>
            )}
          </div>
          <div className="pointer-events-none absolute right-5 bottom-5 left-5 flex items-end justify-between gap-4">
            <div className="pointer-events-auto max-w-[460px] rounded-xl border bg-card/95 p-4 shadow-md backdrop-blur">
              <IssueList issues={issues} />
            </div>
            {solved.ok ? (
              <div className="pointer-events-auto rounded-xl border bg-card/95 p-4 text-right shadow-md backdrop-blur">
                <p className="font-display text-xl font-semibold tabular">{areaM2(solved.at).toFixed(2)} м²</p>
                <p className="text-xs text-muted-foreground">
                  Өнцөг: {solved.angles.map((a) => `${a.toFixed(1)}°`).join(" · ")}
                </p>
                <p className="text-xs text-muted-foreground">Тааз {room.ceiling ? formatMm(room.ceiling.um) : "?"}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
